import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { useQrOwnership } from "../hooks/useIsQrOwner";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import { LogOut, LayoutDashboard } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import EmergencyLayout from "../components/layout/EmergencyLayout";
import QrOwnerDashboard from "../components/qr/QrOwnerDashboard";
import { LoadingState, ErrorState } from "../components/ui/states";
import { COMPANY } from "../config/company";

const LandingPage = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, logout } = useAuth();
  const { isOwner, checking: ownershipChecking } = useQrOwnership(qrId);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [locket, setLocket] = useState(null);
  const [showReportDisclaimer, setShowReportDisclaimer] = useState(false);
  const [publicInfo, setPublicInfo] = useState(null);

  useEffect(() => {
    const fetchLocket = async () => {
      setLoading(true);
      setError(null);
      setErrorCode(null);
      try {
        let res;
        if (shouldUseMockData()) {
          res = await mockApiService.getLocketStatus(qrId);
        } else {
          res = await api.get(`/qr/${qrId}`);
        }
        setLocket(res.data);
      } catch (err) {
        setErrorCode(err.response?.data?.code || null);
        setError(err.response?.data?.error || "QR sticker not found or server error.");
      } finally {
        setLoading(false);
      }
    };
    if (qrId) fetchLocket();
  }, [qrId]);

  // Sticker scans open /:qrId: activated stickers always go to the public emergency info
  // (even for a signed-in owner), un-activated ones go to setup. The owner dashboard is
  // only shown for in-app navigation that passes { state: { ownerDashboard: true } }.
  const wantsOwnerDashboard = Boolean(location.state?.ownerDashboard);
  const redirectTo = !locket
    ? null
    : locket.needsSetup
      ? `/${qrId}/setup`
      : locket.isActivated && (!wantsOwnerDashboard || (!ownershipChecking && !isOwner))
        ? `/${qrId}/public`
        : null;

  useEffect(() => {
    if (redirectTo) navigate(redirectTo, { replace: true });
  }, [redirectTo, navigate]);

  useEffect(() => {
    if (!qrId || !locket?.isActivated || !isOwner) return;
    const fetchPublicInfo = async () => {
      try {
        let res;
        if (shouldUseMockData()) {
          res = await mockApiService.getPublicEmergencyInfo(qrId);
        } else {
          res = await api.get(`/emergency/${qrId}/public`);
        }
        setPublicInfo(res?.data || res);
      } catch (_) {
        setPublicInfo(null);
      }
    };
    fetchPublicInfo();
  }, [qrId, locket?.isActivated, isOwner]);

  if (loading) return <LoadingState label="Loading QR sticker..." emergencyCalls />;

  if (error) {
    return (
      <ErrorState
        title={errorCode === "QR_DELETED" ? "QR sticker no longer active" : "QR not found"}
        message={error}
        emergencyCalls
      />
    );
  }

  if (!locket) return null;

  if (redirectTo || (locket.isActivated && wantsOwnerDashboard && ownershipChecking)) {
    return (
      <LoadingState
        label={locket.needsSetup ? "Opening sticker setup..." : "Opening emergency information..."}
        emergencyCalls={!locket.needsSetup}
      />
    );
  }

  if (locket.isActivated) {
    const ownerName = publicInfo?.customer?.name || "";
    const ownerPhoto = publicInfo?.customer?.photo || null;

    return (
      <EmergencyLayout
        showLogo
        maxWidth="max-w-[980px]"
        className="qr-owner-page"
        headerRight={
          token ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="min-h-12 min-w-12 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
                aria-label={t("nav.profile")}
                title={t("nav.profile")}
              >
                <LayoutDashboard size={20} />
              </button>
              {isOwner ? (
                <button
                  type="button"
                  onClick={() => {
                    logout();
                  }}
                  className="min-h-12 min-w-12 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
                  aria-label="Logout"
                >
                  <LogOut size={20} />
                </button>
              ) : null}
            </div>
          ) : null
        }
      >
        <QrOwnerDashboard
          qrId={qrId}
          isOwner={isOwner}
          token={token}
          ownerName={ownerName}
          ownerPhoto={ownerPhoto}
          publicInfo={publicInfo}
          onReportOpen={() => setShowReportDisclaimer(true)}
          showReportDisclaimer={showReportDisclaimer}
          onReportClose={() => setShowReportDisclaimer(false)}
          onReportConfirm={() => {
            setShowReportDisclaimer(false);
            navigate(`/${qrId}/report`);
          }}
        />
      </EmergencyLayout>
    );
  }

  if (locket.error) {
    return <ErrorState title="Error" message={locket.message || locket.error} emergencyCalls />;
  }

  return (
    <ErrorState
      title="Status unclear"
      message={`We found this QR code but could not determine its status. Email ${COMPANY.supportEmail} for help.`}
      emergencyCalls
    />
  );
};

export default LandingPage;
